import { create } from 'zustand';
import {
  getOrders,
  getOrdersOverview,
  getReleases,
  type OrderSummary,
  type OrdersOverview,
  type ReleasesResponse,
} from '@/api/orders';

/**
 * The two reads every work view is built on — the journey list and the
 * server-computed overview — held once for the whole app.
 *
 * In sundae this lived inside OrdersPage, which was fine when the page was one
 * mounted component switching an internal `view` string. Here the views are
 * real routes, so keeping the data in the component would refetch 4 MB of
 * overview on every nav click. The shell loads once, refreshes on focus and on
 * `orders.updated`, and every route reads from here.
 */
interface OrdersDataState {
  orders: OrderSummary[];
  overview: OrdersOverview | null;
  /**
   * The Make-live queue, read with the overview so its nav count is as fresh
   * as every other queue's. It comes over a separate door (the live catalog)
   * and fails on its own: a catalog outage leaves the overview intact and
   * only this count missing. Null until read, or for users who cannot open
   * the queue at all (`withReleases`).
   */
  releases: ReleasesResponse | null;
  releasesError: string | null;
  withReleases: boolean;
  loading: boolean;
  error: string | null;
  includeArchived: boolean;
  lastLoadedAt: number;
  load: (options?: { includeArchived?: boolean; withReleases?: boolean }) => Promise<void>;
  /** Refetch only if the data is older than `maxAgeMs`. */
  refreshIfStale: (maxAgeMs: number) => void;
  setIncludeArchived: (include: boolean) => void;
  /** Local patch after an inline label edit, so the row doesn't wait on a reload. */
  patchOrder: (id: string, patch: Partial<OrderSummary>) => void;
}

let inFlight: Promise<void> | null = null;

export const useOrdersDataStore = create<OrdersDataState>()((set, get) => ({
  orders: [],
  overview: null,
  releases: null,
  releasesError: null,
  withReleases: false,
  loading: true,
  error: null,
  includeArchived: false,
  lastLoadedAt: 0,

  load: async (options = {}) => {
    const includeArchived = options.includeArchived ?? get().includeArchived;
    const withReleases = options.withReleases ?? get().withReleases;
    // Concurrent callers (shell mount + a view's onChanged) share one round trip.
    if (inFlight && includeArchived === get().includeArchived && withReleases === get().withReleases) {
      return inFlight;
    }
    set({ loading: true, error: null, includeArchived, withReleases });
    const run = (async () => {
      // A failed releases read keeps the last good rows and records why; it
      // never takes the overview down with it.
      const releasesRead: Promise<Partial<OrdersDataState>> = withReleases
        ? getReleases().then(
          (releases) => ({ releases, releasesError: null }),
          (error: unknown) => ({ releasesError: error instanceof Error ? error.message : 'Failed to load releases' })
        )
        : Promise.resolve({ releases: null, releasesError: null });
      try {
        const [orders, overview, releasesState] = await Promise.all([
          getOrders({ includeArchived }),
          getOrdersOverview(),
          releasesRead,
        ]);
        set({ orders, overview, ...releasesState, lastLoadedAt: Date.now() });
      } catch (error) {
        set({ error: error instanceof Error ? error.message : 'Failed to load orders' });
      } finally {
        set({ loading: false });
        inFlight = null;
      }
    })();
    inFlight = run;
    return run;
  },

  refreshIfStale: (maxAgeMs) => {
    const { lastLoadedAt, load } = get();
    if (Date.now() - lastLoadedAt < maxAgeMs) return;
    void load();
  },

  setIncludeArchived: (includeArchived) => {
    if (get().includeArchived === includeArchived) return;
    void get().load({ includeArchived });
  },

  patchOrder: (id, patch) =>
    set((state) => ({
      orders: state.orders.map((order) => (order.id === id ? { ...order, ...patch } : order)),
    })),
}));
